import { FiPrinter, FiX } from "react-icons/fi";
import { FaCheckCircle } from "react-icons/fa";

/**
 * PaymentReceipt — reusable receipt modal
 *
 * Props:
 *  isOpen       : boolean
 *  onClose      : () => void
 *  receiptData  : {
 *    orderId, orderNumber, date,
 *    customer: { name, phone },
 *    items: [{ name, quantity, price, total }],
 *    totals: {
 *      subtotal, discount, gst, net, paid,
 *      originalDue, roundedDue, roundOffDifference,
 *      adjustment, dueAfterAdjustment,
 *      amountPaid, balanceRefunded, creditApplied
 *    },
 *    payment: { method, reference, notes }
 *  }
 */
const PaymentReceipt = ({ isOpen, onClose, receiptData }) => {
  if (!isOpen || !receiptData) return null;

  const formatCurrency = (val) => `₹${Number(val || 0).toFixed(2)}`;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt #${receiptData.orderNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .receipt-box { max-width: 800px; margin: auto; padding: 20px; border: 1px solid #eee; }
            .header { text-align: center; margin-bottom: 20px; }
            .row { display: flex; justify-content: space-between; margin-bottom: 6px; }
            .total { font-weight: bold; margin-top: 15px; border-top: 1px solid #ccc; padding-top: 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <div class="receipt-box">
            <div class="header"><h2>PAYMENT RECEIPT</h2></div>
            <p>Order #${receiptData.orderNumber}<br/>
              ${new Date(receiptData.date).toLocaleString("en-IN", {
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit", hour12: true,
              })}
            </p>
            <p><strong>Customer:</strong> ${receiptData.customer.name} (${receiptData.customer.phone})</p>
            <table>
              <thead>
                <tr><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr>
              </thead>
              <tbody>
                ${receiptData.items.map((item) => `
                  <tr>
                    <td>${item.name}
                    ${item.addons?.map((addon, aIdx) => (
                      <div key={`addon-${aIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {addon.name} – ₹{addon.addon_price.toFixed(2)}
                      </div>
                    ))}
                    ${item.products?.map((product, aIdx) => (
                      <div key={`product-${aIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {product.name} x {product.quantity} – ₹{product.total_price.toFixed(2)}
                      </div>
                    ))}
                    ${item.otherCharges?.map((charge, cIdx) => (
                      <div key={`charge-${cIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {charge.description} – ₹{charge.amount.toFixed(2)}
                      </div>
                    ))}
                    </td>
                    <td>${item.quantity}</td>
                    <td>₹${Number(item.price || 0).toFixed(2)}</td>
                    <td>₹${Number(item.total || 0).toFixed(2)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
            <div class="row"><span>Subtotal:</span><span>₹${Number(receiptData.totals.subtotal || 0).toFixed(2)}</span></div>
            ${receiptData.totals.discount > 0 ? `<div class="row"><span>Discount:</span><span>-₹${Number(receiptData.totals.discount || 0).toFixed(2)}</span></div>` : ""}
            <div class="row"><span>GST:</span><span>+₹${Number(receiptData.totals.gst || 0).toFixed(2)}</span></div>
            <div class="row"><span>Order Total:</span><span>₹${Number(receiptData.totals.net || 0).toFixed(2)}</span></div>
            ${receiptData.totals.paid > 0 ? `<div class="row"><span>Already Paid:</span><span>-₹${Number(receiptData.totals.paid || 0).toFixed(2)}</span></div>` : ""}
            ${receiptData.totals.adjustment > 0 ? `<div class="row"><span>Adjustment:</span><span>-₹${Number(receiptData.totals.adjustment || 0).toFixed(2)}</span></div>` : ""}
            <div class="row total"><span>Amount Payable:</span><span>₹${Number(receiptData.totals.dueAfterAdjustment || 0).toFixed(0)}</span></div>
            <div class="row"><span>Amount Received:</span><span>₹${Number(receiptData.totals.amountPaid || 0).toFixed(0)}</span></div>
            ${receiptData.totals.balanceRefunded > 0 ? `<div class="row"><span>Refunded:</span><span>₹${Number(receiptData.totals.balanceRefunded || 0).toFixed(0)}</span></div>` : ""}
            ${receiptData.totals.creditApplied > 0 ? `<div class="row"><span>Credit Added:</span><span>₹${Number(receiptData.totals.creditApplied || 0).toFixed(0)}</span></div>` : ""}
            <div class="row"><span>Payment Method:</span><span>${receiptData.payment.method?.toUpperCase()} ${receiptData.payment.reference ? `(${receiptData.payment.reference})` : ""}</span></div>
            ${receiptData.payment.notes ? `<div class="row"><span>Notes:</span><span>${receiptData.payment.notes}</span></div>` : ""}
          </div>
          <script>window.onload = () => setTimeout(() => window.print(), 500);</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto p-4">

        {/* Header */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <FaCheckCircle className="text-green-500 w-5 h-5" />
            <h2 className="text-lg font-bold">Payment Receipt</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-blue-700"
            >
              <FiPrinter size={14} /> Print
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 bg-gray-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-gray-700"
            >
              <FiX size={14} /> Close
            </button>
          </div>
        </div>

        {/* Receipt Body */}
        <div className="p-3 border rounded-lg text-sm">

          {/* Order Info */}
          <div className="text-start mb-4">
            <h3 className="text-xl font-bold">RECEIPT</h3>
            <p className="text-gray-600">
              Order #{receiptData.orderNumber}
              <br />
              {new Date(receiptData.date).toLocaleString("en-IN", {
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit", hour12: true,
              })}
            </p>
          </div>

          {/* Customer */}
          <div className="mb-3">
            <strong>Customer:</strong>{" "}
            {receiptData.customer.name}
            {receiptData.customer.phone && ` (${receiptData.customer.phone})`}
          </div>

          {/* Items Table */}
          <table className="w-full border-collapse border border-gray-300 mb-3 text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-1 text-left">Item</th>
                <th className="border p-1 text-left">Qty</th>
                {/* <th className="border p-1 text-left">Price</th> */}
                <th className="border p-1 text-left">Total</th>
              </tr>
            </thead>
            <tbody>
              {receiptData.items.map((item, idx) => (
                <tr key={idx}>
                  <td className="border p-1">
                    {/* Main item name */}
                    <div style={{ fontWeight: "500" }}>{item.name}</div>

                    {/* Addons (indented) */}
                    {item.addons?.map((addon, aIdx) => (
                      <div key={`addon-${aIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {addon.name} – ₹{addon.addon_price.toFixed(2)}
                      </div>
                    ))}
                    
                    {/* Addons (indented) */}
                    {item.products?.map((product, aIdx) => (
                      <div key={`product-${aIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {product.name} x {product.quantity} – ₹{product.total_price.toFixed(2)}
                      </div>
                    ))}

                    {/* Other charges (indented) */}
                    {item.otherCharges?.map((charge, cIdx) => (
                      <div key={`charge-${cIdx}`} style={{ paddingLeft: "20px", fontSize: "12px", color: "#555" }}>
                        • {charge.description} – ₹{charge.amount.toFixed(2)}
                      </div>
                    ))}</td>
                  <td className="border p-1">{item.quantity}</td>
                  {/* <td className="border p-1">{formatCurrency(item.price)}</td> */}
                  <td className="border p-1">{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{formatCurrency(receiptData.totals.subtotal)}</span>
            </div>
            {receiptData.totals.discount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Discount:</span>
                <span>-{formatCurrency(receiptData.totals.discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-green-600">
              <span>GST:</span>
              <span>+{formatCurrency(receiptData.totals.gst)}</span>
            </div>
            <div className="flex justify-between font-semibold border-t pt-1">
              <span>Order Total:</span>
              <span>{formatCurrency(receiptData.totals.net)}</span>
            </div>
            {receiptData.totals.paid > 0 && (
              <>
                <div className="flex justify-between text-blue-600">
                  <span>Already Paid:</span>
                  <span>-{formatCurrency(receiptData.totals.paid)}</span>
                </div>
                {/* <div className="flex justify-between">
                  <span>Due before round-off:</span>
                  <span>{formatCurrency(receiptData.totals.originalDue)}</span>
                </div> */}
              </>
            )}
            {/* <div className="flex justify-between text-purple-600">
              <span>
                Round-off (
                {receiptData.totals.roundOffDifference >= 0 ? "+" : ""}
                {formatCurrency(receiptData.totals.roundOffDifference)}):
              </span>
              <span>{formatCurrency(receiptData.totals.roundedDue)}</span>
            </div> */}
            {receiptData.totals.adjustment > 0 && (
              <div className="flex justify-between text-yellow-600">
                <span>Adjustment:</span>
                <span>-{formatCurrency(receiptData.totals.adjustment)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-green-700 border-t pt-1">
              <span>Amount Payable:</span>
              <span>{formatCurrency(receiptData.totals.dueAfterAdjustment)}</span>
            </div>
            <div className="flex justify-between">
              <span>Amount Received:</span>
              <span>{formatCurrency(receiptData.totals.amountPaid)}</span>
            </div>
            {receiptData.totals.balanceRefunded > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Refunded:</span>
                <span>{formatCurrency(receiptData.totals.balanceRefunded)}</span>
              </div>
            )}
            {receiptData.totals.creditApplied > 0 && (
              <div className="flex justify-between text-blue-600">
                <span>Credit Added:</span>
                <span>{formatCurrency(receiptData.totals.creditApplied)}</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-1">
              <span>Payment Method:</span>
              <span>
                {receiptData.payment.method?.toUpperCase()}{" "}
                {receiptData.payment.reference ? `(${receiptData.payment.reference})` : ""}
              </span>
            </div>
            {receiptData.payment.notes && (
              <div className="flex justify-between">
                <span>Notes:</span>
                <span>{receiptData.payment.notes}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReceipt;